/**
 * Settings Modal Component
 * 
 * 환경설정 모달 - 계정 연동, 계정 관리, 보안, 표시 설정, MFA, 알림, 약관 설정
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../auth';
import { useLanguage, Language } from '../i18n';
import { useTheme, Theme } from '../theme';
import { analytics } from '../analytics';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const AUTH_TOKEN_KEY = 'auth_token';

// 콘텐츠 영역 최소 높이 (모든 탭에서 동일하게 유지 - 비밀번호 변경 폼 기준)
const CONTENT_MIN_HEIGHT = 520;

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'accountLinking' | 'accountManagement' | 'security' | 'display' | 'mfa' | 'notifications' | 'terms';

type GithubStatus = {
  connected: boolean;
  username?: string | null;
  connected_at?: string | null;
  scope?: string | null;
};

type MergeState = {
  merge_preauth: string;
  provider: 'github';
  source_user?: { email?: string | null; name?: string | null; picture?: string | null } | null;
};

const MERGE_NOTICE_KEY = 'releasegate_merge_logout_notice';

// 비밀번호 정책 검사
function checkPasswordPolicy(password: string) {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(password),
  };
}

function isPasswordValid(password: string): boolean {
  const checks = checkPasswordPolicy(password);
  return Object.values(checks).every(Boolean);
}

// 토글 스위치 컴포넌트
function ToggleSwitch({ 
  enabled, 
  onChange, 
  disabled = false 
}: { 
  enabled: boolean; 
  onChange: () => void; 
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-status-go/50 focus:ring-offset-2 focus:ring-offset-terminal-bg ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${enabled ? 'bg-status-go' : 'bg-gray-300 dark:bg-terminal-dim/60'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full shadow-md transition-transform ${
          enabled ? 'translate-x-6 bg-white' : 'translate-x-1 bg-white'
        }`}
      />
    </button>
  );
}

// 커스텀 드롭다운 컴포넌트
function CustomDropdown<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  minWidth = 120,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; icon?: string }[];
  disabled?: boolean;
  minWidth?: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 감지
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={dropdownRef} className="relative" style={{ minWidth }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm text-left flex items-center justify-between gap-2 transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-status-go/50 cursor-pointer'
        } ${isOpen ? 'border-status-go/50' : ''}`}
      >
        <span className="flex items-center gap-2">
          {selectedOption?.icon && <span>{selectedOption.icon}</span>}
          {selectedOption?.label}
        </span>
        <svg 
          className={`w-4 h-4 text-terminal-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-full bg-terminal-bg border border-terminal-border rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
                value === option.value
                  ? 'bg-status-go/10 text-status-go'
                  : 'text-terminal-text hover:bg-terminal-dim/30'
              }`}
            >
              {option.icon && <span>{option.icon}</span>}
              <span>{option.label}</span>
              {value === option.value && (
                <svg className="w-4 h-4 ml-auto text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { t, lang, setLang } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { user, refreshUser, logout, deleteAccount } = useAuth();
  
  // 약관 URL
  const termsUrl = lang === 'ko' ? '/terms/ko.html' : '/terms/en.html';
  const privacyUrl = lang === 'ko' ? '/privacy/ko.html' : '/privacy/en.html';
  const marketingUrl = lang === 'ko' ? '/marketing/ko.html' : '/marketing/en.html';
  
  // 탭 상태
  const [activeTab, setActiveTab] = useState<Tab>('accountLinking');
  
  // UI 상태
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  
  // GitHub 연동 상태
  const [githubStatus, setGithubStatus] = useState<GithubStatus | null>(null);
  const [githubLoading, setGithubLoading] = useState(false);
  const [githubActionLoading, setGithubActionLoading] = useState(false);
  const [showGithubConnectConfirm, setShowGithubConnectConfirm] = useState(false);
  const [mergeState, setMergeState] = useState<MergeState | null>(null);
  const [mergeLoading, setMergeLoading] = useState(false);

  // MFA 상태 (UI만, 백엔드 연동 필요)
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [emailOtpEnabled, setEmailOtpEnabled] = useState(false);

  // 약관 상태 (서버와 동기화)
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [termsUpdating, setTermsUpdating] = useState(false);

  // 계정 관리 상태
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTeamWarning, setShowTeamWarning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 비밀번호 변경 상태 (이메일 사용자만)
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 로그인 기록 상태
  type LoginHistoryItem = {
    id: number;
    ip_address: string | null;
    device_type: string | null;
    browser: string | null;
    os: string | null;
    login_method: string;
    success: boolean;
    created_at: string;
  };
  const [loginHistory, setLoginHistory] = useState<LoginHistoryItem[]>([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);

  // 시간대 설정 상태 (localStorage에 저장)
  const TIMEZONE_KEY = 'releasegate_timezone';
  const [timezone, setTimezoneState] = useState<string>(() => {
    return localStorage.getItem(TIMEZONE_KEY) || 'auto';
  });
  
  const setTimezone = (tz: string) => {
    setTimezoneState(tz);
    localStorage.setItem(TIMEZONE_KEY, tz);
  };
  
  // 실제 적용할 시간대 (auto인 경우 브라우저 시간대)
  const effectiveTimezone = timezone === 'auto' 
    ? Intl.DateTimeFormat().resolvedOptions().timeZone 
    : timezone;

  // 토스트 자동 숨김
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // 모달 열릴 때만 탭 초기화 (isOpen 변경 시에만)
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setActiveTab('accountLinking');
    }
  }, [isOpen]);

  // 마케팅 동의 상태 동기화 (user 변경 시 또는 모달 열릴 때)
  useEffect(() => {
    if (user && isOpen) {
      setMarketingAgreed(user.marketing_agreed);
    }
  }, [user?.marketing_agreed, isOpen]);

  // 보안 탭 활성화 시 로그인 기록 로드
  useEffect(() => {
    const fetchLoginHistory = async () => {
      if (activeTab !== 'security' || !isOpen) return;
      
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      
      setLoginHistoryLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/auth/login-history?limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setLoginHistory(data.items || []);
        }
      } catch {
        // ignore
      } finally {
        setLoginHistoryLoading(false);
      }
    };
    
    fetchLoginHistory();
  }, [activeTab, isOpen]);

  // GitHub 연동 상태 조회
  useEffect(() => {
    const fetchGithubStatus = async () => {
      if (!isOpen) return;
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        setGithubStatus(null);
        return;
      }

      setGithubLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/integrations/github/status`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setGithubStatus(data);
        } else {
          if (res.status === 401) {
            try {
              window.dispatchEvent(new CustomEvent('releasegate:auth_invalid', { detail: { reason: 'github_status_401' } }));
            } catch {
              // ignore
            }
          }
          setGithubStatus(null);
        }
      } catch {
        setGithubStatus(null);
      } finally {
        setGithubLoading(false);
      }
    };

    fetchGithubStatus();
  }, [isOpen]);

  // GitHub OAuth 콜백에서 전달된 merge_preauth 처리
  useEffect(() => {
    if (!isOpen) return;
    try {
      const token = localStorage.getItem('releasegate_merge_preauth');
      const provider = localStorage.getItem('releasegate_merge_provider');
      // GitHub provider만 지원
      if (token && provider === 'github') {
        let sourceEmail: string | null = null;
        try {
          const [, payloadB64] = token.split('.');
          if (payloadB64) {
            const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
            const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
            const json = JSON.parse(atob(padded));
            sourceEmail = json.source_email ?? null;
          }
        } catch {
          // ignore
        }
        setMergeState({ merge_preauth: token, provider: 'github', source_user: sourceEmail ? { email: sourceEmail } : null });
        localStorage.removeItem('releasegate_merge_preauth');
        localStorage.removeItem('releasegate_merge_provider');
      }
    } catch {
      // ignore
    }
  }, [isOpen]);
  
  if (!isOpen || !user) return null;
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleGithubConnect = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      onClose();
      window.history.pushState({}, '', '/error/401');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }

    try {
      localStorage.setItem('releasegate_open_settings', '1');
    } catch {
      // ignore
    }

    const u = new URL(window.location.href);
    u.searchParams.set('settings', 'open');
    const returnTo = u.toString();
    setGithubActionLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/integrations/github/authorize-url?return_to=${encodeURIComponent(returnTo)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        onClose();
        window.history.pushState({}, '', '/error/401');
        window.dispatchEvent(new PopStateEvent('popstate'));
        return;
      }

      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.detail || t.settings?.connectFailed || 'Failed to connect');
      }

      const data: { authorize_url: string } = await res.json();
      window.location.href = data.authorize_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : t.settings?.connectFailed || 'Failed to connect');
    } finally {
      setGithubActionLoading(false);
    }
  };

  const openGithubConnectConfirm = () => {
    setShowGithubConnectConfirm(true);
  };

  const handleGithubDisconnect = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    setGithubActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/integrations/github/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(t.settings?.disconnectFailed || 'Failed to disconnect');
      }

      setGithubStatus({ connected: false });
    } catch (e) {
      setError(e instanceof Error ? e.message : t.settings?.disconnectFailed || 'Failed to disconnect');
    } finally {
      setGithubActionLoading(false);
    }
  };

  const handleConfirmMerge = async (keep: 'source' | 'target') => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token || !mergeState) return;

    setMergeLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/merge/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ merge_preauth: mergeState.merge_preauth, keep }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.detail || '계정 통합에 실패했습니다');
      }
      const data = await res.json().catch(() => null);
      if (data?.access_token) {
        try {
          localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
          if (data?.user) {
            localStorage.setItem('auth_user', JSON.stringify(data.user));
          }
        } catch {
          // ignore
        }
        await refreshUser();
        setMergeState(null);
        onClose();
        return;
      }

      try {
        localStorage.setItem(
          MERGE_NOTICE_KEY,
          JSON.stringify({
            kept_user_email: data?.kept_user_email ?? null,
            kept_user_id: data?.kept_user_id ?? null,
          }),
        );
      } catch {
        // ignore
      }
      logout();
      onClose();
      setMergeState(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '계정 통합에 실패했습니다');
    } finally {
      setMergeLoading(false);
    }
  };

  // 마케팅 동의 토글
  const handleMarketingToggle = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    const newValue = !marketingAgreed;
    setTermsUpdating(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ marketing_agreed: newValue }),
      });

      if (!res.ok) {
        throw new Error('Failed to update marketing consent');
      }

      setMarketingAgreed(newValue);
      await refreshUser();
      
      // 토스트 메시지 표시 (날짜/시각 포함)
      const now = new Date();
      const dateTimeStr = now.toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
      
      const baseMessage = newValue 
        ? (t.settings?.marketingAgreedToast || '마케팅 정보 수신에 동의하였습니다.')
        : (t.settings?.marketingWithdrawnToast || '마케팅 정보 수신 동의를 철회하였습니다.');
      
      setToast(`${baseMessage} (${dateTimeStr})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
      // 실패 시 원래 값으로 복구
      setMarketingAgreed(!newValue);
    } finally {
      setTermsUpdating(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'accountLinking', label: t.settings?.accountLinking || '계정 연동' },
    { id: 'accountManagement', label: t.settings?.accountManagement || '계정 관리' },
    { id: 'security', label: t.settings?.security || '보안' },
    { id: 'display', label: t.settings?.display || '표시 설정' },
    { id: 'mfa', label: t.settings?.mfa || 'MFA' },
    { id: 'terms', label: t.settings?.termsTab || '약관 보기' },
    { id: 'notifications', label: t.settings?.notifications || '알림' },
  ];
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-terminal-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-terminal-text">
            {t.settings?.title || '환경설정'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-terminal-muted hover:text-terminal-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* 본문 */}
        <div className="flex flex-1 overflow-hidden">
          {/* 사이드바 탭 */}
          <div className="w-36 border-r border-terminal-border bg-terminal-dim/10 flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full px-4 py-3 text-left text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-status-go/10 text-status-go border-r-2 border-status-go font-medium'
                    : 'text-terminal-muted hover:text-terminal-text hover:bg-terminal-dim/20'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* 탭 컨텐츠 */}
          <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: CONTENT_MIN_HEIGHT }}>
            {/* 에러 메시지 */}
            {error && (
              <div className="mb-4 p-3 bg-status-nogo/10 border border-status-nogo/30 rounded-lg text-status-nogo text-sm">
                {error}
              </div>
            )}

            {/* 계정 연동 탭 */}
            {activeTab === 'accountLinking' && (
              <div className="space-y-6">
                {/* GitHub 계정 */}
                <div className="flex items-center justify-between gap-4 p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#24292f] dark:bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <svg className="w-6 h-6 text-white dark:text-[#24292f]" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-terminal-text">
                        {t.settings?.githubAccount || 'GitHub 계정'}
                      </div>
                      <div className="text-xs text-terminal-muted">
                        {githubLoading
                          ? '...'
                          : githubStatus?.connected
                            ? `@${githubStatus.username}`
                            : (t.settings?.notConnected || '연결 안 됨')}
                      </div>
                    </div>
                  </div>
                  <div>
                    {githubLoading ? (
                      <div className="text-xs text-terminal-muted">...</div>
                    ) : githubStatus?.connected ? (
                      <button
                        onClick={handleGithubDisconnect}
                        disabled={githubActionLoading}
                        className="px-4 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors disabled:opacity-50"
                      >
                        {githubActionLoading ? (t.settings?.disconnecting || '해제 중...') : (t.settings?.disconnect || '연결 해제')}
                      </button>
                    ) : (
                      <button
                        onClick={openGithubConnectConfirm}
                        className="px-4 py-2 bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 rounded-lg text-status-go text-xs font-medium transition-colors"
                      >
                        {t.settings?.connect || '연결'}
                      </button>
                    )}
                  </div>
                </div>

                {/* GitHub Organization 접근 권한 안내 */}
                {githubStatus?.connected && (
                  <div className="p-4 bg-terminal-dim/5 rounded-lg border border-terminal-border/50">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-terminal-muted">
                        {t.profile.githubOrgAccessQuestion}
                      </div>
                      <a
                        href="https://github.com/settings/connections/applications"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-md text-xs text-terminal-text transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t.profile.githubOrgAccessSettings}
                      </a>
                    </div>
                    <div className="mt-2 text-xs text-terminal-muted/70">
                      → <span className="text-status-warning font-medium">Release Gate</span> {t.profile.githubOrgAccessHint}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 계정 관리 탭 */}
            {activeTab === 'accountManagement' && (
              <div className="space-y-6">
                {/* 비밀번호 변경 - 이메일 사용자만 */}
                {user.email && !user.google_connected && !user.github_connected && (
                  <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-terminal-text">
                          {t.settings?.changePassword || '비밀번호 변경'}
                        </div>
                        <div className="text-xs text-terminal-muted mt-0.5">
                          {t.settings?.changePasswordDesc || '계정 보안을 위해 주기적으로 비밀번호를 변경하세요.'}
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                        className="px-4 py-2 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors"
                      >
                        {t.settings?.changePasswordButton || '비밀번호 변경'}
                      </button>
                    </div>
                    
                    {showPasswordForm && (
                      <div className="mt-4 pt-4 border-t border-terminal-border/50 space-y-3">
                        <div>
                          <label className="block text-xs text-terminal-muted mb-1">
                            {t.settings?.currentPassword || '현재 비밀번호'}
                          </label>
                          <div className="relative">
                            <input
                              type={showCurrentPassword ? 'text' : 'password'}
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="w-full px-3 py-2 pr-10 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-terminal-muted hover:text-terminal-text transition-colors"
                            >
                              {showCurrentPassword ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="flex items-center gap-1.5 text-xs text-terminal-muted mb-1">
                            {t.settings?.newPassword || '새 비밀번호'}
                            {/* 비밀번호 정책 툴팁 */}
                            <div className="relative group">
                              <svg 
                                className={`w-3.5 h-3.5 cursor-help ${newPassword && isPasswordValid(newPassword) ? 'text-status-go' : 'text-terminal-muted'}`}
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                                <div className="p-3 bg-terminal-bg border border-terminal-border rounded-lg shadow-xl text-xs space-y-1 whitespace-nowrap">
                                  <div className="text-terminal-muted mb-2 font-medium">{t.auth.passwordPolicy}</div>
                                  <div className={checkPasswordPolicy(newPassword).minLength ? 'text-status-go' : 'text-terminal-muted'}>
                                    {checkPasswordPolicy(newPassword).minLength ? '✓' : '○'} {t.auth.passwordMinLength}
                                  </div>
                                  <div className={checkPasswordPolicy(newPassword).hasUppercase ? 'text-status-go' : 'text-terminal-muted'}>
                                    {checkPasswordPolicy(newPassword).hasUppercase ? '✓' : '○'} {t.auth.passwordUppercase}
                                  </div>
                                  <div className={checkPasswordPolicy(newPassword).hasLowercase ? 'text-status-go' : 'text-terminal-muted'}>
                                    {checkPasswordPolicy(newPassword).hasLowercase ? '✓' : '○'} {t.auth.passwordLowercase}
                                  </div>
                                  <div className={checkPasswordPolicy(newPassword).hasNumber ? 'text-status-go' : 'text-terminal-muted'}>
                                    {checkPasswordPolicy(newPassword).hasNumber ? '✓' : '○'} {t.auth.passwordNumber}
                                  </div>
                                  <div className={checkPasswordPolicy(newPassword).hasSpecial ? 'text-status-go' : 'text-terminal-muted'}>
                                    {checkPasswordPolicy(newPassword).hasSpecial ? '✓' : '○'} {t.auth.passwordSpecial}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? 'text' : 'password'}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className={`w-full px-3 py-2 pr-10 bg-terminal-dim/20 border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none transition-colors ${
                                newPassword 
                                  ? (isPasswordValid(newPassword) && !(currentPassword && currentPassword === newPassword))
                                    ? 'border-status-go/50' 
                                    : 'border-status-nogo/50'
                                  : 'border-terminal-border focus:border-status-go/50'
                              }`}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-terminal-muted hover:text-terminal-text transition-colors"
                            >
                              {showNewPassword ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {currentPassword && newPassword && currentPassword === newPassword && (
                            <p className="text-[10px] text-status-nogo mt-1">
                              {lang === 'ko' ? '현재 비밀번호와 동일한 비밀번호로는 변경할 수 없습니다' : 'New password cannot be the same as current password'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-terminal-muted mb-1">
                            {t.settings?.confirmPassword || '비밀번호 확인'}
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className={`w-full px-3 py-2 pr-10 bg-terminal-dim/20 border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none transition-colors ${
                                confirmPassword 
                                  ? newPassword === confirmPassword 
                                    ? 'border-status-go/50' 
                                    : 'border-status-nogo/50'
                                  : 'border-terminal-border focus:border-status-go/50'
                              }`}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-terminal-muted hover:text-terminal-text transition-colors"
                            >
                              {showConfirmPassword ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {confirmPassword && newPassword !== confirmPassword && (
                            <p className="text-[10px] text-status-nogo mt-1">
                              {t.settings?.passwordMismatch || '비밀번호가 일치하지 않습니다'}
                            </p>
                          )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                          <button
                            onClick={() => {
                              setShowPasswordForm(false);
                              setCurrentPassword('');
                              setNewPassword('');
                              setConfirmPassword('');
                            }}
                            className="px-4 py-2 text-terminal-muted text-xs hover:text-terminal-text transition-colors"
                          >
                            {t.settings?.cancel || '취소'}
                          </button>
                          <button
                            onClick={async () => {
                              if (newPassword !== confirmPassword) {
                                setError(t.settings?.passwordMismatch || '새 비밀번호가 일치하지 않습니다.');
                                return;
                              }
                              setPasswordChanging(true);
                              setError(null);
                              try {
                                const token = localStorage.getItem(AUTH_TOKEN_KEY);
                                const res = await fetch(`${API_BASE}/api/auth/change-password`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`,
                                  },
                                  body: JSON.stringify({
                                    current_password: currentPassword,
                                    new_password: newPassword,
                                  }),
                                });
                                if (!res.ok) {
                                  const payload = await res.json().catch(() => null);
                                  throw new Error(payload?.detail || t.settings?.passwordChangeFailed || '비밀번호 변경에 실패했습니다.');
                                }
                                setToast(t.settings?.passwordChanged || '비밀번호가 변경되었습니다.');
                                setShowPasswordForm(false);
                                setCurrentPassword('');
                                setNewPassword('');
                                setConfirmPassword('');
                              } catch (e) {
                                setError(e instanceof Error ? e.message : t.settings?.passwordChangeFailed || '비밀번호 변경에 실패했습니다.');
                              } finally {
                                setPasswordChanging(false);
                              }
                            }}
                            disabled={passwordChanging || !currentPassword || !isPasswordValid(newPassword) || newPassword !== confirmPassword || (!!currentPassword && !!newPassword && currentPassword === newPassword)}
                            className="px-4 py-2 bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 rounded-lg text-status-go text-xs font-medium transition-colors disabled:opacity-50"
                          >
                            {passwordChanging ? (t.settings?.saving || '저장 중...') : (t.settings?.save || '저장')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* OAuth 사용자용 안내 */}
                {(user.google_connected || user.github_connected) && (
                  <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-terminal-dim/30 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-terminal-text">
                          {t.settings?.changePassword || '비밀번호 변경'}
                        </div>
                        <div className="text-xs text-terminal-muted mt-0.5">
                          {lang === 'ko' 
                            ? 'OAuth 계정으로 로그인 중입니다. 비밀번호는 연결된 서비스(Google, GitHub)에서 관리하세요.'
                            : 'You are logged in with OAuth. Manage your password through the connected service (Google, GitHub).'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 회원 탈퇴 */}
                <div className="p-4 bg-status-nogo/5 rounded-lg border border-status-nogo/30">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-status-nogo">
                        {t.settings?.deleteAccount || '회원 탈퇴'}
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.deleteAccountDesc || '계정을 삭제하면 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.'}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (user.team_id) {
                          setShowTeamWarning(true);
                        } else {
                          setShowDeleteConfirm(true);
                        }
                      }}
                      className="px-4 py-2 bg-status-nogo/10 hover:bg-status-nogo/20 border border-status-nogo/50 rounded-lg text-status-nogo text-xs font-medium transition-colors"
                    >
                      {t.settings?.deleteAccountButton || '탈퇴하기'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 보안 탭 */}
            {activeTab === 'security' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-medium text-terminal-text">
                      {t.settings?.loginHistory || '로그인 기록'}
                    </h3>
                    <p className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.loginHistoryDesc || '최근 로그인 활동을 확인할 수 있습니다.'}
                    </p>
                  </div>
                </div>

                {loginHistoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="w-6 h-6 animate-spin text-terminal-muted" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : loginHistory.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-terminal-muted text-sm">
                    {t.settings?.noLoginHistory || '로그인 기록이 없습니다.'}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                    {loginHistory.map((item, index) => (
                      <div 
                        key={item.id} 
                        className={`p-3 rounded-lg border ${
                          index === 0 
                            ? 'bg-status-go/5 border-status-go/30' 
                            : 'bg-terminal-dim/10 border-terminal-border'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2.5">
                            {/* 기기 아이콘 */}
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              index === 0 ? 'bg-status-go/20' : 'bg-terminal-dim/30'
                            }`}>
                              {item.device_type === 'mobile' ? (
                                <svg className={`w-4 h-4 ${index === 0 ? 'text-status-go' : 'text-terminal-muted'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              ) : item.device_type === 'tablet' ? (
                                <svg className={`w-4 h-4 ${index === 0 ? 'text-status-go' : 'text-terminal-muted'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              ) : (
                                <svg className={`w-4 h-4 ${index === 0 ? 'text-status-go' : 'text-terminal-muted'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-terminal-text">
                                  {item.browser || 'Unknown'} / {item.os || 'Unknown'}
                                </span>
                                {index === 0 && (
                                  <span className="px-1.5 py-0.5 bg-status-go/20 text-status-go rounded text-[10px]">
                                    {t.settings?.currentSession || '현재 세션'}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-terminal-muted">
                                  {item.login_method === 'google' ? '🔵 Google' : 
                                   item.login_method === 'github' ? '⚫ GitHub' : 
                                   '📧 Email'}
                                </span>
                                {item.ip_address && (
                                  <span className="text-[10px] text-terminal-muted">
                                    IP: {item.ip_address}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] text-terminal-muted">
                              {new Date(item.created_at + (item.created_at.endsWith('Z') ? '' : 'Z')).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US', {
                                timeZone: effectiveTimezone,
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 표시 설정 탭 */}
            {activeTab === 'display' && (
              <div className="space-y-6">
                {/* 언어 설정 */}
                <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-terminal-text">
                        {t.settings?.languageSetting || '언어'}
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.languageSettingDesc || '인터페이스 언어를 선택하세요.'}
                      </div>
                    </div>
                    <CustomDropdown<Language>
                      value={lang}
                      onChange={(newLang) => {
                        analytics.languageChange(newLang);
                        setLang(newLang);
                      }}
                      options={[
                        { value: 'ko', label: t.language.ko, icon: '🇰🇷' },
                        { value: 'en', label: t.language.en, icon: '🇺🇸' },
                      ]}
                      minWidth={140}
                    />
                  </div>
                </div>

                {/* 테마 설정 */}
                <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-terminal-text">
                        {t.settings?.themeSetting || '테마'}
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.themeSettingDesc || '화면 테마를 선택하세요.'}
                      </div>
                    </div>
                    <CustomDropdown<Theme>
                      value={theme}
                      onChange={setTheme}
                      options={[
                        { value: 'light', label: t.settings?.themeLight || t.theme.light, icon: '☀️' },
                        { value: 'dark', label: t.settings?.themeDark || t.theme.dark, icon: '🌙' },
                        { value: 'system', label: t.settings?.themeSystem || t.theme.system, icon: '💻' },
                      ]}
                      minWidth={160}
                    />
                  </div>
                </div>

                {/* 시간대 설정 */}
                <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-terminal-text">
                        {t.settings?.timezoneSetting || '시간대'}
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {lang === 'ko' 
                          ? `적용: ${effectiveTimezone}`
                          : `Applied: ${effectiveTimezone}`}
                      </div>
                    </div>
                    <CustomDropdown<string>
                      value={timezone}
                      onChange={setTimezone}
                      options={[
                        { value: 'auto', label: lang === 'ko' ? '자동 (브라우저)' : 'Auto (Browser)', icon: '🌐' },
                        { value: 'Asia/Seoul', label: lang === 'ko' ? '서울 (KST)' : 'Seoul (KST)', icon: '🇰🇷' },
                        { value: 'Asia/Tokyo', label: lang === 'ko' ? '도쿄 (JST)' : 'Tokyo (JST)', icon: '🇯🇵' },
                        { value: 'America/New_York', label: lang === 'ko' ? '뉴욕 (EST)' : 'New York (EST)', icon: '🇺🇸' },
                        { value: 'America/Los_Angeles', label: lang === 'ko' ? 'LA (PST)' : 'LA (PST)', icon: '🇺🇸' },
                        { value: 'Europe/London', label: lang === 'ko' ? '런던 (GMT)' : 'London (GMT)', icon: '🇬🇧' },
                        { value: 'UTC', label: 'UTC', icon: '🌍' },
                      ]}
                      minWidth={180}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* MFA 탭 */}
            {activeTab === 'mfa' && (
              <div className="space-y-6">
                <div className="text-sm text-terminal-muted mb-4">
                  {t.settings?.mfaDescription || '다중 인증(MFA)을 활성화하면 계정 보안이 강화됩니다.'}
                </div>

                {/* MFA 전체 토글 */}
                <div className="flex items-center justify-between p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.mfaEnabled || 'MFA 활성화'}
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={mfaEnabled}
                    onChange={() => setMfaEnabled(!mfaEnabled)}
                  />
                </div>

                {/* MFA 옵션들 */}
                <div className={`space-y-4 ${!mfaEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                  {/* 이메일 OTP (비활성화) */}
                  <div className="flex items-center justify-between p-4 bg-terminal-dim/5 rounded-lg border border-terminal-border/50 opacity-50">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-terminal-text flex items-center gap-2">
                        {t.settings?.emailOtp || '이메일 OTP'}
                        <span className="px-1.5 py-0.5 bg-terminal-dim/30 rounded text-[10px] text-terminal-muted">
                          {t.settings?.smsOtpDisabled || '준비 중'}
                        </span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.emailOtpDesc || '로그인 시 이메일로 인증 코드를 받습니다.'}
                      </div>
                    </div>
                    <ToggleSwitch
                      enabled={emailOtpEnabled}
                      onChange={() => setEmailOtpEnabled(!emailOtpEnabled)}
                      disabled={true}
                    />
                  </div>

                  {/* SMS OTP (비활성화) */}
                  <div className="flex items-center justify-between p-4 bg-terminal-dim/5 rounded-lg border border-terminal-border/50 opacity-50">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-terminal-text flex items-center gap-2">
                        {t.settings?.smsOtp || 'SMS OTP'}
                        <span className="px-1.5 py-0.5 bg-terminal-dim/30 rounded text-[10px] text-terminal-muted">
                          {t.settings?.smsOtpDisabled || '준비 중'}
                        </span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.smsOtpDesc || '로그인 시 휴대폰 문자로 인증 코드를 받습니다.'}
                      </div>
                    </div>
                    <ToggleSwitch
                      enabled={false}
                      onChange={() => {}}
                      disabled={true}
                    />
                  </div>

                  {/* TOTP (비활성화) */}
                  <div className="flex items-center justify-between p-4 bg-terminal-dim/5 rounded-lg border border-terminal-border/50 opacity-50">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-terminal-text flex items-center gap-2">
                        {t.settings?.totp || 'TOTP 인증 앱'}
                        <span className="px-1.5 py-0.5 bg-terminal-dim/30 rounded text-[10px] text-terminal-muted">
                          {t.settings?.totpDisabled || '준비 중'}
                        </span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.totpDesc || '인증 앱에서 생성한 코드로 인증합니다.'}
                      </div>
                      <div className="text-[10px] text-terminal-muted/70 mt-1">
                        {t.settings?.totpApps || 'Google Authenticator, Authy 등'}
                      </div>
                    </div>
                    <ToggleSwitch
                      enabled={false}
                      onChange={() => {}}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 약관 보기 탭 */}
            {activeTab === 'terms' && (
              <div className="space-y-6">
                <div className="text-sm text-terminal-muted mb-4">
                  {t.settings?.termsDescription || '현재 동의한 약관 및 정책을 확인하고 관리할 수 있습니다.'}
                </div>

                {/* 이용약관 (필수, 읽기 전용) */}
                <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-terminal-text">
                          {t.settings?.termsOfService || '이용약관'}
                        </span>
                        <span className="px-1.5 py-0.5 bg-status-go/20 text-status-go rounded text-[10px]">
                          {t.settings?.required || '필수'}
                        </span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.termsOfServiceDesc || '서비스 이용에 필요한 필수 약관입니다.'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${user.terms_agreed ? 'text-status-go' : 'text-terminal-muted'}`}>
                        {user.terms_agreed ? (t.settings?.agreed || '동의함') : (t.settings?.withdrawn || '동의 안 함')}
                      </span>
                      <ToggleSwitch
                        enabled={user.terms_agreed}
                        onChange={() => {}}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-terminal-border/50">
                    <a
                      href={`${termsUrl}?popup=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-status-go hover:underline"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {lang === 'ko' ? '약관 전문 보기' : 'View full terms'}
                    </a>
                  </div>
                </div>

                {/* 개인정보처리방침 (필수, 읽기 전용) */}
                <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-terminal-text">
                          {t.settings?.privacyPolicy || '개인정보처리방침'}
                        </span>
                        <span className="px-1.5 py-0.5 bg-status-go/20 text-status-go rounded text-[10px]">
                          {t.settings?.required || '필수'}
                        </span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.privacyPolicyDesc || '개인정보 수집 및 이용에 대한 필수 동의입니다.'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${user.privacy_agreed ? 'text-status-go' : 'text-terminal-muted'}`}>
                        {user.privacy_agreed ? (t.settings?.agreed || '동의함') : (t.settings?.withdrawn || '동의 안 함')}
                      </span>
                      <ToggleSwitch
                        enabled={user.privacy_agreed}
                        onChange={() => {}}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-terminal-border/50">
                    <a
                      href={`${privacyUrl}?popup=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-status-go hover:underline"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {lang === 'ko' ? '약관 전문 보기' : 'View full policy'}
                    </a>
                  </div>
                </div>

                {/* 마케팅 정보 수신 (선택, 변경 가능) */}
                <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-terminal-text">
                          {t.settings?.marketingConsent || '마케팅 정보 수신'}
                        </span>
                        <span className="px-1.5 py-0.5 bg-terminal-dim/30 text-terminal-muted rounded text-[10px]">
                          {t.settings?.optional || '선택'}
                        </span>
                      </div>
                      <div className="text-xs text-terminal-muted mt-0.5">
                        {t.settings?.marketingConsentDesc || '이벤트, 혜택 등 마케팅 정보를 받아보실 수 있습니다.'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${marketingAgreed ? 'text-status-go' : 'text-terminal-muted'}`}>
                        {marketingAgreed ? (t.settings?.agreed || '동의함') : (t.settings?.withdrawn || '동의 안 함')}
                      </span>
                      <ToggleSwitch
                        enabled={marketingAgreed}
                        onChange={handleMarketingToggle}
                        disabled={termsUpdating}
                      />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-terminal-border/50">
                    <a
                      href={`${marketingUrl}?popup=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-status-go hover:underline"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {lang === 'ko' ? '약관 전문 보기' : 'View full terms'}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* 알림 탭 */}
            {activeTab === 'notifications' && (
              <div className="space-y-3 max-h-[472px] overflow-y-auto pr-1">
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-terminal-text">
                    {t.settings?.emailNotifications || '이메일 알림'}
                  </h3>
                  <p className="text-xs text-terminal-muted mt-0.5">
                    {t.settings?.emailNotificationsDesc || '받고 싶은 이메일 알림을 선택하세요.'}
                  </p>
                </div>

                {/* 평가 완료 알림 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifyEvaluation || '평가 완료 알림'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifyEvaluationDesc || 'Go/No-Go 평가 결과를 이메일로 받습니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>

                {/* 팀 초대 알림 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifyTeamInvite || '팀 초대 알림'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifyTeamInviteDesc || '팀 초대를 받았을 때 이메일로 알려드립니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>

                {/* 팀 활동 알림 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifyTeamActivity || '팀 활동 알림'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifyTeamActivityDesc || '팀원이 평가를 완료했을 때 알려드립니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>

                {/* 구독 알림 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifySubscription || '구독 알림'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifySubscriptionDesc || '결제 실패, 갱신 예정, 플랜 변경 안내를 받습니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>

                {/* 보안 알림 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifySecurity || '보안 알림'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifySecurityDesc || '새 기기 로그인, 비밀번호 변경 시 알려드립니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>

                {/* 뉴스레터 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifyNewsletter || '뉴스레터'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifyNewsletterDesc || '소프트웨어 뉴스를 아침/점심/저녁에 받습니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>

                {/* 제품 업데이트 */}
                <div className="flex items-center justify-between p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                  <div>
                    <div className="text-sm font-medium text-terminal-text">
                      {t.settings?.notifyProductUpdate || '제품 업데이트'}
                    </div>
                    <div className="text-xs text-terminal-muted mt-0.5">
                      {t.settings?.notifyProductUpdateDesc || '새 기능, 개선사항 안내를 받습니다.'}
                    </div>
                  </div>
                  <ToggleSwitch enabled={false} onChange={() => {}} disabled={true} />
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* 토스트 메시지 */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="px-4 py-3 bg-terminal-bg border border-terminal-border rounded-lg shadow-xl flex items-center gap-2">
            <svg className="w-5 h-5 text-status-go flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-terminal-text">{toast}</span>
          </div>
        </div>
      )}

      {/* GitHub 연결 확인 팝업 */}
      {showGithubConnectConfirm && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !githubActionLoading) {
              setShowGithubConnectConfirm(false);
            }
          }}
        >
          <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 pb-4 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-terminal-dim/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">🔐</span>
              </div>
              <h3 className="text-lg font-semibold text-terminal-text mb-2">
                {t.profile.githubConnectConfirmTitle}
              </h3>
              <p className="text-sm text-terminal-muted leading-relaxed">
                {t.profile.githubConnectConfirmDesc}
              </p>
              <div className="mt-3 p-3 bg-terminal-dim/20 border border-terminal-border rounded-lg text-left">
                <p className="text-xs text-terminal-muted leading-relaxed">
                  {t.profile.githubConnectDifferentAccountHint}
                </p>
              </div>
            </div>

            <div className="px-6 pb-6 flex flex-col gap-2">
              <button
                onClick={async () => {
                  setShowGithubConnectConfirm(false);
                  await handleGithubConnect();
                }}
                disabled={githubActionLoading}
                className="w-full py-3 bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 rounded-lg text-status-go text-sm font-medium transition-colors disabled:opacity-50"
              >
                {githubActionLoading ? t.profile.githubConnecting : t.profile.githubUseCurrentAccount}
              </button>
              <button
                onClick={() => {
                  window.open('https://github.com/logout', '_blank', 'noopener,noreferrer');
                }}
                disabled={githubActionLoading}
                className="w-full py-3 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-sm font-medium transition-colors disabled:opacity-50"
              >
                {t.profile.githubOpenLogoutPage}
              </button>
              <button
                onClick={() => setShowGithubConnectConfirm(false)}
                disabled={githubActionLoading}
                className="w-full py-2 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
              >
                {t.profile.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 팀 소속 시 탈퇴 제한 안내 팝업 */}
      {showTeamWarning && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTeamWarning(false);
            }
          }}
        >
          <div className="bg-terminal-bg border border-status-warning/50 rounded-xl shadow-2xl w-full max-w-sm animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 pb-4 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-status-warning/20 rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-status-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-status-warning mb-2">
                {t.profile.leaveTeamFirstTitle}
              </h3>
            </div>
            
            <div className="px-6 pb-4">
              <div className="p-4 bg-status-warning/10 border border-status-warning/30 rounded-lg space-y-2">
                <p className="text-sm text-status-warning/90 leading-relaxed">
                  {t.profile.leaveTeamFirstMessage}
                </p>
                <p className="text-xs text-terminal-muted">
                  {t.profile.leaveTeamFirstHint}
                </p>
              </div>
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowTeamWarning(false)}
                className="w-full py-3 bg-status-warning/20 hover:bg-status-warning/30 border border-status-warning/50 rounded-lg text-status-warning text-sm font-medium transition-colors"
              >
                {t.profile.understood}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 탈퇴 확인 팝업 */}
      {showDeleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isDeleting) {
              setShowDeleteConfirm(false);
            }
          }}
        >
          <div className="bg-terminal-bg border border-status-nogo/50 rounded-xl shadow-2xl w-full max-w-sm animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 pb-4 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-status-nogo/20 rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-status-nogo" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-status-nogo mb-2">
                {t.profile.deleteWarningTitle}
              </h3>
            </div>
            
            <div className="px-6 pb-4">
              <div className="p-4 bg-status-nogo/10 border border-status-nogo/30 rounded-lg">
                <p className="text-sm text-status-nogo/90 leading-relaxed">
                  {t.profile.deleteWarning}
                </p>
              </div>
            </div>
            
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={async () => {
                  setIsDeleting(true);
                  setError(null);
                  try {
                    await deleteAccount();
                    onClose();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : t.profile.deleteFailed);
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
                className="flex-1 py-3 border border-status-nogo/50 rounded-lg text-status-nogo/80 hover:text-status-nogo hover:border-status-nogo text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t.profile.deleting}
                  </>
                ) : t.profile.confirmDelete}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setError(null);
                }}
                disabled={isDeleting}
                className="flex-1 py-3 bg-status-go hover:bg-status-go/90 border border-status-go rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                {t.profile.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 계정 통합 확인 팝업 */}
      {mergeState && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !mergeLoading) setMergeState(null);
          }}
        >
          <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 pb-4 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-terminal-dim/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">🧩</span>
              </div>
              <h3 className="text-lg font-semibold text-terminal-text mb-2">
                계정 통합이 필요합니다
              </h3>
              <p className="text-sm text-terminal-muted leading-relaxed">
                이 계정은 이미 다른 ReleaseGate 계정에 연결되어 있어요. 통합하면 한 계정으로 사용할 수 있습니다.
              </p>
              <p className="mt-2 text-xs text-status-nogo/80">
                ⚠️ 통합 버튼을 누르면 두 계정 중 한 계정이 통합 후 비활성화(삭제 처리)됩니다. 어떤 계정을 유지할지 신중히 선택해주세요.
              </p>
              {mergeState.source_user?.email && (
                <div className="mt-3 p-3 bg-terminal-dim/20 border border-terminal-border rounded-lg text-left">
                  <p className="text-xs text-terminal-muted">
                    통합 대상: {mergeState.source_user.email}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 pb-6 flex flex-col gap-2">
              <button
                onClick={() => handleConfirmMerge('source')}
                disabled={mergeLoading}
                className="w-full py-3 bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 rounded-lg text-status-go text-sm font-medium transition-colors disabled:opacity-50"
              >
                {mergeLoading ? '통합 중...' : '기존(주) 계정 유지하고 통합'}
              </button>
              <p className="text-[11px] text-terminal-muted -mt-1">
                기존 계정(이미 연결된 계정)을 유지하고, <span className="text-terminal-text">현재 계정은 통합 후 비활성화</span>됩니다.
              </p>
              <button
                onClick={() => handleConfirmMerge('target')}
                disabled={mergeLoading}
                className="w-full py-3 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-sm font-medium transition-colors disabled:opacity-50"
              >
                {mergeLoading ? '통합 중...' : '현재 계정 유지하고 통합'}
              </button>
              <p className="text-[11px] text-terminal-muted -mt-1">
                현재 계정을 유지하고, <span className="text-terminal-text">기존 계정은 통합 후 비활성화</span>됩니다.
              </p>
              <button
                onClick={() => setMergeState(null)}
                disabled={mergeLoading}
                className="w-full py-2 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
